import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Filter, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQ = { withCredentials: true };
const PAGE_SIZE = 20;

const OfflineInventoryDashboard = () => {
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({
    search: '',
    airline_code: '',
    status: '',
    from_airport: '',
    to_airport: ''
  });

  const fetchFlights = async (pageOverride) => {
    const p = pageOverride ?? page;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search.trim()) params.append('search', filters.search.trim());
      if (filters.airline_code) params.append('airline_code', filters.airline_code);
      if (filters.status) params.append('status', filters.status);
      if (filters.from_airport) params.append('from_airport', filters.from_airport);
      if (filters.to_airport) params.append('to_airport', filters.to_airport);
      params.append('skip', String((p - 1) * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);

      const response = await axios.get(`${API}/admin/offline-flights?${params.toString()}`, REQ);
      if (response.data.success) {
        setFlights(response.data.data);
        setTotal(typeof response.data.total === 'number' ? response.data.total : response.data.data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching flights:', error);
      alert('Failed to fetch flights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlights(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount only; pagination uses explicit handlers
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleDelete = async (flightId) => {
    if (
      !window.confirm(
        'Deactivate this offline flight? It will be hidden from active lists; existing bookings are not deleted.'
      )
    )
      return;

    try {
      const response = await axios.delete(`${API}/admin/offline-flights/${flightId}`, REQ);
      if (response.data.success) {
        alert(response.data.message || 'Flight deactivated');
        fetchFlights();
      }
    } catch (error) {
      console.error('Error deactivating flight:', error);
      alert(error.response?.data?.detail || 'Failed to deactivate flight');
    }
  };

  const calculateTotalSeats = (inventory) => {
    return inventory.reduce((sum, inv) => sum + (inv.total_seats || 0), 0);
  };

  const calculateSoldSeats = (inventory) => {
    return inventory.reduce((sum, inv) => sum + (inv.sold_seats || 0), 0);
  };

  const calculateAvailableSeats = (inventory) => {
    return inventory.reduce((sum, inv) => {
      if (typeof inv.available_seats === 'number') return sum + inv.available_seats;
      const t = inv.total_seats || 0;
      const s = inv.sold_seats || 0;
      return sum + Math.max(0, t - s);
    }, 0);
  };

  const applyFilters = () => {
    setPage(1);
    fetchFlights(1);
  };

  const handleClear = () => {
    setFilters({ search: '', airline_code: '', status: '', from_airport: '', to_airport: '' });
    setSortBy('created_at');
    setSortDir('desc');
    setPage(1);
    fetchFlights(1);
  };

  const goPage = (next) => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    setPage(clamped);
    fetchFlights(clamped);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1B375B]">Offline Inventory Management</h1>
            <p className="text-gray-600 mt-1">Manage block tickets and offline flights</p>
          </div>
          <button
            onClick={() => navigate('/admin/offline-inventory/create')}
            className="flex items-center gap-2 px-6 py-3 bg-[#659A9B] hover:bg-[#1B375B] text-white font-semibold rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add New Flight
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Search ID, flight no., airline…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
          />
          <input
            type="text"
            placeholder="Airline Code (e.g., 6E)"
            value={filters.airline_code}
            onChange={(e) => setFilters({ ...filters, airline_code: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
          />
          <input
            type="text"
            placeholder="From Airport (e.g., DEL)"
            value={filters.from_airport}
            onChange={(e) => setFilters({ ...filters, from_airport: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
          />
          <input
            type="text"
            placeholder="To Airport (e.g., BOM)"
            value={filters.to_airport}
            onChange={(e) => setFilters({ ...filters, to_airport: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-4 mt-4 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="created_at">Created</option>
              <option value="flight_number">Flight number</option>
              <option value="airline_code">Airline code</option>
              <option value="id">Flight ID</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Direction</label>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
          <button
            onClick={applyFilters}
            className="flex items-center gap-2 px-4 py-2 bg-[#1B375B] text-white rounded-lg hover:bg-[#659A9B] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* Flights Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-[#659A9B] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading flights...</p>
          </div>
        ) : flights.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 text-lg">No offline flights found</p>
            <button
              onClick={() => navigate('/admin/offline-inventory/create')}
              className="mt-4 text-[#659A9B] hover:text-[#1B375B] font-semibold"
            >
              Create your first flight
            </button>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100 flex justify-between items-center">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Flight</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Route</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Schedule</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Inventory</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Fares</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {flights.map((flight) => (
                    <tr key={flight.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-gray-900">{flight.airline_name}</p>
                          <p className="text-sm text-gray-500">{flight.flight_number}</p>
                          <p className="text-xs text-gray-400">{flight.flight_type === 'direct' ? 'Non-Stop' : 'Via'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {flight.route.from_airport} → {flight.route.to_airport}
                        </p>
                        <p className="text-xs text-gray-500">
                          {flight.route.from_city} - {flight.route.to_city}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-700 capitalize">{flight.schedule_type}</p>
                        <p className="text-xs text-gray-500">{flight.inventory.length} dates</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="font-semibold text-gray-900">
                            Total: {calculateTotalSeats(flight.inventory)}
                          </p>
                          <p className="text-green-600">Sold: {calculateSoldSeats(flight.inventory)}</p>
                          <p className="text-blue-600">Available: {calculateAvailableSeats(flight.inventory)}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-700">ADT: ₹{flight.fares.adult}</p>
                          <p className="text-gray-600">CHD: ₹{flight.fares.child}</p>
                          <p className="text-gray-500">INF: ₹{flight.fares.infant}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            flight.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {flight.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/offline-inventory/${flight.id}/inventory`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Inventory"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/offline-inventory/${flight.id}/edit`)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(flight.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Deactivate (soft)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 py-4 border-t border-gray-100">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => goPage(page - 1)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => goPage(page + 1)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-40"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OfflineInventoryDashboard;
